import { action, computed, makeObservable, observable } from 'mobx'

import BsonObjectId from '~/client/src/shared/utils/BsonObjectId'

import {
  DEFAULT_MARKUP_STYLE,
  IMarkupFeatureData,
  NO_FILL,
} from './MarkupDrawController'

export type TextAlign = 'left' | 'center' | 'right'

export interface ITextBox {
  id: string
  lng: number
  lat: number
  text: string
  color: string
  fontSize: number
  bold: boolean
  italic: boolean
  underline: boolean
  textAlign: TextAlign
  /** null = auto-width to content. */
  boxWidth: number | null
  rotation: number
  /** NO_FILL = transparent. */
  fillColor: string
  globeViewId?: string
}

interface ITextStyle {
  color: string
  fillColor: string
  fontSize: number
  bold: boolean
  italic: boolean
  underline: boolean
  textAlign: TextAlign
}

export const DEFAULT_FONT_SIZE = 14
const MIN_BOX_WIDTH = 40

/**
 * Owns text markups for an edit session. Text is HTML (not Terra Draw geometry),
 * so it lives here rather than in MarkupDrawController, but round-trips through
 * the same save/load seam via IMarkupFeatureData (kind === 'text', Point).
 */
export default class MarkupTextStore {
  @observable public boxes: ITextBox[] = []
  @observable public editingId: string | null = null
  @observable public selectedId: string | null = null
  @observable private currentStyle: ITextStyle = {
    color: DEFAULT_MARKUP_STYLE.color,
    fillColor: NO_FILL,
    fontSize: DEFAULT_FONT_SIZE,
    bold: true,
    italic: false,
    underline: false,
    textAlign: 'left',
  }

  private readonly onChange?: () => void
  // Told of every selection change, inside the action that makes it.
  public onSelectionChange?: (prev: string | null, next: string | null) => void
  private readonly getGlobeViewId?: () => string | undefined

  public constructor(
    onChange?: () => void,
    getGlobeViewId?: () => string | undefined,
  ) {
    this.onChange = onChange
    this.getGlobeViewId = getGlobeViewId
    makeObservable(this)
  }

  @computed
  public get selected(): ITextBox | null {
    return this.boxes.find(box => box.id === this.selectedId) ?? null
  }

  // Every selection write. Moving the selection off a box drops it if it is still empty, which
  // its blur cannot be trusted to do: an unmounting box never blurs. restore/load replace the
  // boxes wholesale and keep undo's own record, so they pass discardPrev = false.
  private setSelected(id: string | null, discardPrev: boolean): void {
    const prev = this.selectedId
    this.selectedId = id
    if (prev === id) return
    if (discardPrev && prev != null) this.discardIfEmpty(prev)
    this.onSelectionChange?.(prev, id)
  }

  private find(id: string): ITextBox | undefined {
    return this.boxes.find(box => box.id === id)
  }

  @action.bound
  public place(lng: number, lat: number, rotation = 0): string {
    const id = BsonObjectId.generate()
    this.boxes.push({
      id,
      lng,
      lat,
      text: '',
      boxWidth: null,
      rotation,
      globeViewId: this.getGlobeViewId?.(),
      ...this.currentStyle,
    })
    this.editingId = id
    this.setSelected(id, true)
    // Skip history for an empty box; the first snapshot fires on first keystroke.
    return id
  }

  @action.bound
  public addBox(box: ITextBox): void {
    // Paste/duplicate lands on the current globe (like commitFeature for geometry).
    this.boxes.push({ ...box, globeViewId: this.getGlobeViewId?.() })
    this.setSelected(box.id, true)
    this.editingId = null
    this.onChange?.()
  }

  @action.bound
  public updateText(id: string, text: string): void {
    const box = this.find(id)
    if (box) {
      box.text = text
      this.onChange?.()
    }
  }

  @action.bound
  public select(id: string | null): void {
    this.setSelected(id, true)
    if (id == null || id !== this.editingId) this.editingId = null
  }

  @action.bound
  public edit(id: string): void {
    // Enter edit only on an already-selected box. On iOS a single tap can fire
    // onDoubleClick with no preceding onClick, so the box isn't selected yet —
    // without this guard the first tap jumps straight to edit, bypassing select, and
    // the move/rotate handles never appear. Route the first interaction to select; a
    // double-tap on the already-selected box then edits.
    if (this.selectedId !== id) {
      this.select(id)
      return
    }
    this.editingId = id
  }

  @action.bound
  public stopEditing(): void {
    this.editingId = null
  }

  @action.bound
  public move(id: string, lng: number, lat: number): void {
    const box = this.find(id)
    if (box) {
      box.lng = lng
      box.lat = lat
      this.onChange?.()
    }
  }

  @action.bound
  public setBoxWidth(id: string, px: number | null): void {
    const box = this.find(id)
    if (box) {
      box.boxWidth = px == null ? null : Math.max(MIN_BOX_WIDTH, px)
      this.onChange?.()
    }
  }

  @action.bound
  public setRotation(id: string, deg: number): void {
    const box = this.find(id)
    if (box) {
      box.rotation = deg
      this.onChange?.()
    }
  }

  @action.bound
  public setColor(color: string): void {
    this.currentStyle.color = color
    if (this.selected) this.selected.color = color
    this.onChange?.()
  }

  @action.bound
  public setFillColor(fillColor: string): void {
    this.currentStyle.fillColor = fillColor
    if (this.selected) this.selected.fillColor = fillColor
    this.onChange?.()
  }

  @action.bound
  public setFontSize(fontSize: number): void {
    this.currentStyle.fontSize = fontSize
    if (this.selected) this.selected.fontSize = fontSize
    this.onChange?.()
  }

  @action.bound
  public toggleBold(): void {
    const next = !(this.selected?.bold ?? this.currentStyle.bold)
    this.currentStyle.bold = next
    if (this.selected) this.selected.bold = next
    this.onChange?.()
  }

  @action.bound
  public toggleItalic(): void {
    const next = !(this.selected?.italic ?? this.currentStyle.italic)
    this.currentStyle.italic = next
    if (this.selected) this.selected.italic = next
    this.onChange?.()
  }

  @action.bound
  public toggleUnderline(): void {
    const next = !(this.selected?.underline ?? this.currentStyle.underline)
    this.currentStyle.underline = next
    if (this.selected) this.selected.underline = next
    this.onChange?.()
  }

  @action.bound
  public deleteSelected(): void {
    if (this.selectedId == null) return
    this.remove(this.selectedId)
  }

  @action.bound
  public remove(id: string): void {
    this.boxes = this.boxes.filter(box => box.id !== id)
    if (this.selectedId === id) this.setSelected(null, false)
    if (this.editingId === id) this.editingId = null
    this.onChange?.()
  }

  /** Replace all boxes (undo/redo restore); does not fire onChange. */
  @action.bound
  public restore(boxes: ITextBox[]): void {
    this.boxes = boxes.map(box => ({ ...box }))
    this.editingId = null
    this.setSelected(null, false)
  }

  /** Drop an empty box (called on blur and defensively before save). */
  @action.bound
  public discardIfEmpty(id: string): void {
    const box = this.find(id)
    if (box && !box.text.trim()) this.remove(id)
  }

  @action.bound
  public discardAllEmpty(): void {
    this.boxes = this.boxes.filter(box => box.text.trim())
  }

  public getMarkupFeatures(): IMarkupFeatureData[] {
    return this.boxes
      .filter(box => box.text.trim())
      .map(box => ({
        id: box.id,
        kind: 'text' as const,
        geometryType: 'Point',
        coordinates: [{ longitude: box.lng, latitude: box.lat }],
        color: box.color,
        fillColor: box.fillColor,
        strokeWidth: 0,
        opacity: 1,
        text: box.text,
        fontSize: box.fontSize,
        bold: box.bold,
        italic: box.italic,
        underline: box.underline,
        textAlign: box.textAlign,
        boxWidth: box.boxWidth ?? undefined,
        rotation: box.rotation || undefined,
        globeViewId: box.globeViewId,
      }))
  }

  @action.bound
  public load(markups: IMarkupFeatureData[]): void {
    this.boxes = markups.map(markup => ({
      id: markup.id,
      lng: markup.coordinates[0]?.longitude ?? 0,
      lat: markup.coordinates[0]?.latitude ?? 0,
      text: markup.text ?? '',
      color: markup.color ?? DEFAULT_MARKUP_STYLE.color,
      fillColor: markup.fillColor ?? NO_FILL,
      fontSize: markup.fontSize ?? DEFAULT_FONT_SIZE,
      bold: markup.bold ?? false,
      italic: markup.italic ?? false,
      underline: markup.underline ?? false,
      textAlign: (markup.textAlign as TextAlign) ?? 'left',
      boxWidth: markup.boxWidth ?? null,
      rotation: markup.rotation ?? 0,
      globeViewId: markup.globeViewId,
    }))
    this.editingId = null
    this.setSelected(null, false)
  }
}
